import React from "react";
import Logo from "../../../lib/assets/images/jureb-logo.png";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import pathnames from "../../../constants/pathnames";
import { Link, useLocation } from "react-router-dom";
import OrganizationIcon from "../../assets/icons/OrganizationIcon";
import UsersIcon from "../../assets/icons/Users";
import PromoCodeIcon from "../../assets/icons/PromoCodeIcon";
import IntegrationsIcon from "../../assets/icons/IntegrationsIcon";
import BillCheckIcon from "../../assets/icons/BillCheck";
import PushNotificationIcon from "../../assets/icons/PushNotificationIcon";
import AdminUserIcon from "../../assets/icons/AdminUserIcon";
import MarketplaceIcon from "../../assets/icons/MarketplaceIcon";
import SidebarCollapsibleItem, { isGroupActive } from "./SidebarCollapsibleItem";
import { isMarketplaceEnabled } from "../../../config";

const SIDEBAR_LINKS = [
  {
    name: "Overview",
    path: pathnames.DASHBOARD,
    Icon: GridViewOutlinedIcon,
  },
  {
    name: "Organization Management",
    path: pathnames.ORGANIZATIONS,
    Icon: OrganizationIcon,
    children: [
      { name: "Businesses", path: pathnames.ORGANIZATIONS },
      { name: "Users", path: pathnames.USERS },
      { name: "Payments", path: pathnames.PAYMENTS },
    ],
  },
  {
    name: "Referral Management",
    path: pathnames.REFERRERS,
    Icon: UsersIcon,
    children: [
      { name: "All Referrals", path: pathnames.REFERRERS },
      { name: "Payouts", path: pathnames.PAYOUTS },
    ],
  },
  {
    name: "Promo Code Management",
    path: pathnames.PROMO_CODES,
    Icon: PromoCodeIcon,
    children: [
      { name: "Overview", path: pathnames.PROMO_CODES_OVERVIEW },
      { name: "Redemptions", path: pathnames.PROMO_CODES_REDEMPTIONS },
    ],
  },
  {
    name: "Marketplace",
    path: pathnames.MARKETPLACE,
    Icon: MarketplaceIcon,
    children: [
      { name: "Add-ons", path: pathnames.MARKETPLACE_ADDONS },
      { name: "Payments", path: pathnames.MARKETPLACE_PAYMENTS },
    ],
  },
  {
    name: "Integrations",
    path: pathnames.INTERFACES,
    Icon: IntegrationsIcon,
  },
  {
    name: "FIRS Transmissions",
    path: pathnames.TRANSMISSIONS,
    Icon: BillCheckIcon,
    children: [
      { name: "All Transmissions", path: pathnames.TRANSMISSIONS, matches: [pathnames.API_TRANSMISSIONS] },
      { name: "Unassigned Invoices", path: pathnames.UNASSIGNED_INVOICES },
    ],
  },
  {
    name: "Push Notification",
    path: pathnames.PUSH_NOTIFICATIONS,
    Icon: PushNotificationIcon,
  },
].filter((item) => isMarketplaceEnabled || item.path !== pathnames.MARKETPLACE);

const SIDEBAR_LINKS_II = [
  {
    name: "Administrators",
    path: pathnames.ADMINISTRATORS,
    Icon: AdminUserIcon,
  },
  {
    name: "Settings",
    path: pathnames.SETTINGS_PROFILE,
    Icon: SettingsOutlinedIcon,
  },
];

const AppLayout = ({ children }) => {
  const location = useLocation();
  const pathname = location.pathname;

  // Only one dropdown may be open at a time; default to the group containing the current route
  const activeGroup = SIDEBAR_LINKS.find((item) => item.children && isGroupActive(item, pathname))?.name ?? null;
  const [openGroup, setOpenGroup] = React.useState(activeGroup);

  React.useEffect(() => {
    if (activeGroup) setOpenGroup(activeGroup);
  }, [activeGroup]);
  return (
    <div className="d-flex" style={{ minHeight: "100vh", overflow: "hidden" }}>
      <div
        style={{
          width: 290,
          minWidth: 290,
          maxWidth: 290,
          height: "100vh",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        className="d-none d-lg-block border-end"
      >
        <div className="p-3" style={{ height: "calc(100vh - 100px)" }}>
          <div className="mb-4 d-flex justify-content-center align-items-center">
            <img src={Logo} alt="Jureb" />
          </div>
          <div className="d-flex flex-column justify-content-between gap-4 h-100">
            <div className="">
              <div className="sidebar-nav-section-title">MAIN MENU</div>
              {SIDEBAR_LINKS.map(({ Icon, ...item }, index) => {
                if (item?.children) {
                  return (
                    <SidebarCollapsibleItem
                      key={index}
                      item={{ Icon, ...item }}
                      pathname={pathname}
                      isOpen={openGroup === item.name}
                      onToggle={() => setOpenGroup((prev) => (prev === item.name ? null : item.name))}
                    />
                  );
                }

                const isLinkActive =
                  item?.path === pathnames.DASHBOARD
                    ? pathname === pathnames.DASHBOARD || pathname === "/"
                    : item?.path &&
                      (pathname === item.path || (item.path !== "/" && pathname.startsWith(item.path + "/")));

                return (
                  <Link
                    key={index}
                    to={item?.path}
                    aria-current={isLinkActive ? "page" : undefined}
                    className={`sidebar-nav-item${isLinkActive ? " is-active" : ""}`}
                  >
                    <span className="sidebar-nav-icon">
                      <Icon />
                    </span>
                    <span className="sidebar-nav-label">{item?.name}</span>
                  </Link>
                );
              })}
            </div>
            <div>
              <div className="sidebar-nav-section-title">PREFERENCES</div>
              {SIDEBAR_LINKS_II.map(({ Icon, ...item }, index) => {
                const isLinkActive =
                  item?.path === pathnames.SETTINGS_PROFILE
                    ? pathname.startsWith("/settings")
                    : pathname &&
                      (pathname === item.path || (item.path !== "/" && pathname.startsWith(item.path + "/")));

                return (
                  <Link
                    key={index}
                    to={item?.path}
                    aria-current={isLinkActive ? "page" : undefined}
                    className={`sidebar-nav-item${isLinkActive ? " is-active" : ""}`}
                  >
                    <span className="sidebar-nav-icon">
                      <Icon />
                    </span>
                    <span className="sidebar-nav-label">{item?.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <div id="section-right" className="flex-grow-1" style={{ height: "100vh", overflowY: "auto" }}>
        {children}
      </div>
    </div>
  );
};

export default AppLayout;
